import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit2, Users, BookOpen, LogOut, Eye, Mail, Zap,
  CheckCircle2, AlertCircle, Sparkles, Play, ClipboardList, Settings as SettingsIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import RecipeForm from "@/components/RecipeForm";
import SettingsPanel from "@/components/SettingsPanel";
import {
  adminListAllRecipes,
  adminDeleteRecipe,
  adminPublishRecipe,
  adminUnpublishRecipe,
  adminListSubscribers,
  adminDeleteSubscriber,
  gmailStatus, gmailStartOAuth, gmailDisconnect,
  pipelineLogs, pipelineRunNow,
} from "@/lib/api";

const PIPELINE_DESC = "Daily auto-generation: 6:00am AEST (Australia/Brisbane) · Claude Sonnet 4 · saves as draft only.";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("recipes");
  const [statusFilter, setStatusFilter] = useState("all"); // all | draft | published
  const [recipes, setRecipes] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [gmail, setGmail] = useState({ connected: false });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    document.title = "Admin — JustAirFry";
    // Handle OAuth callback ?gmail=connected etc.
    const gmailParam = searchParams.get("gmail");
    if (gmailParam) {
      if (gmailParam === "connected") toast.success("Gmail connected!");
      else if (gmailParam === "error") toast.error("Gmail connection failed.");
      else if (gmailParam === "invalid_state") toast.error("Gmail state invalid — please retry.");
      // Clean the URL
      const next = new URLSearchParams(searchParams);
      next.delete("gmail");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const refreshGmail = async () => {
    try {
      setGmail(await gmailStatus());
    } catch {
      setGmail({ connected: false });
    }
  };

  const loadRecipes = async () => {
    setLoading(true);
    try {
      const data = await adminListAllRecipes(statusFilter === "all" ? null : statusFilter);
      setRecipes(data);
    } catch {
      toast.error("Failed to load recipes");
    } finally {
      setLoading(false);
    }
  };

  const loadSubscribers = async () => {
    setLoading(true);
    try {
      setSubscribers(await adminListSubscribers());
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    setLoading(true);
    try {
      setLogs(await pipelineLogs(25));
    } catch {
      toast.error("Failed to load logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshGmail();
  }, []);

  useEffect(() => {
    if (tab === "recipes") loadRecipes();
    else if (tab === "subscribers") loadSubscribers();
    else if (tab === "pipeline") loadLogs();
    else setLoading(false); // settings tab loads internally
  }, [tab, statusFilter]); // eslint-disable-line

  const draftCount = recipes.filter((r) => r.status === "draft").length;

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This can't be undone.`)) return;
    try {
      await adminDeleteRecipe(slug);
      toast.success("Recipe deleted");
      loadRecipes();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onPublish = async (slug) => {
    try {
      await adminPublishRecipe(slug);
      toast.success("Published — now live on the site");
      loadRecipes();
    } catch {
      toast.error("Publish failed");
    }
  };

  const onUnpublish = async (slug) => {
    if (!window.confirm(`Unpublish "${slug}"? It will be hidden from the public site.`)) return;
    try {
      await adminUnpublishRecipe(slug);
      toast.success("Moved back to draft");
      loadRecipes();
    } catch {
      toast.error("Unpublish failed");
    }
  };

  const onDeleteSub = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await adminDeleteSubscriber(id);
      toast.success("Subscriber removed");
      loadSubscribers();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const handleConnectGmail = async () => {
    try {
      const { authorization_url } = await gmailStartOAuth();
      window.location.href = authorization_url;
    } catch {
      toast.error("Couldn't start Gmail OAuth");
    }
  };

  const handleDisconnectGmail = async () => {
    if (!window.confirm("Disconnect Gmail? You'll stop receiving daily emails until you reconnect.")) return;
    try {
      await gmailDisconnect();
      toast.success("Gmail disconnected");
      refreshGmail();
    } catch {
      toast.error("Disconnect failed");
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm("Generate a new draft recipe now? Uses one Claude API call.")) return;
    setGenerating(true);
    try {
      const r = await pipelineRunNow();
      if (r.ok) {
        toast.success(`Draft created: ${r.title}`);
        setTab("recipes");
        setStatusFilter("draft");
      } else {
        toast.error("Generation failed — retry scheduled for 10 min.");
      }
      if (tab === "pipeline") loadLogs();
    } catch {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (editing !== null) {
    return (
      <RecipeForm
        recipe={editing === "new" ? null : editing}
        onCancel={() => setEditing(null)}
        onSaved={() => { setEditing(null); loadRecipes(); }}
      />
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--jaf-bg)" }} data-testid="admin-dashboard">
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{ backgroundColor: "rgba(253,251,247,0.92)", borderBottom: "1px solid var(--jaf-border)" }}
      >
        <div className="jaf-container h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden sm:inline-block text-xs uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full"
              style={{ backgroundColor: "var(--jaf-primary)", color: "#fff" }}>
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="view-site-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--jaf-text-muted)" }}>
              <Eye size={14} /> View site
            </Link>
            <span className="hidden md:flex items-center gap-2 text-sm">
              {user?.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> :
                <span className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: "var(--jaf-surface)" }}>{user?.name?.[0] || "?"}</span>}
              <span style={{ color: "var(--jaf-text)" }}>{user?.name}</span>
            </span>
            <button type="button" onClick={handleLogout} data-testid="logout-btn"
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md"
              style={{ border: "1px solid var(--jaf-border)", color: "var(--jaf-text)", backgroundColor: "#fff" }}>
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="jaf-container py-10">
        {/* Pipeline status card */}
        <div className="rounded-xl p-5 mb-8 grid md:grid-cols-3 gap-4"
          style={{ backgroundColor: "#fff", border: "1px solid var(--jaf-border)" }}
          data-testid="pipeline-status-card">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0"
              style={{ backgroundColor: "rgba(200,90,50,0.1)", color: "var(--jaf-primary)" }}>
              <Sparkles size={18} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: "var(--jaf-text-muted)" }}>Daily content pipeline</p>
              <p className="font-serif text-lg leading-tight">Active</p>
              <p className="text-xs mt-1" style={{ color: "var(--jaf-text-muted)" }}>{PIPELINE_DESC}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0`}
              style={{
                backgroundColor: gmail.connected ? "rgba(124,144,130,0.18)" : "rgba(176,68,68,0.12)",
                color: gmail.connected ? "#506357" : "#b04444",
              }}>
              {gmail.connected ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider" style={{ color: "var(--jaf-text-muted)" }}>Gmail notifications</p>
              <p className="font-serif text-lg leading-tight" data-testid="gmail-status-text">
                {gmail.connected ? "Connected" : "Not connected"}
              </p>
              {gmail.connected ? (
                <div className="flex flex-wrap gap-2 mt-1.5">
                  <span className="text-xs truncate" style={{ color: "var(--jaf-text-muted)" }}>{gmail.email}</span>
                  <button type="button" onClick={handleDisconnectGmail} data-testid="gmail-disconnect-btn"
                    className="text-xs underline" style={{ color: "var(--jaf-text-muted)" }}>
                    Disconnect
                  </button>
                </div>
              ) : (
                <button type="button" onClick={handleConnectGmail} data-testid="gmail-connect-btn"
                  className="mt-2 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md"
                  style={{ backgroundColor: "var(--jaf-primary)", color: "#fff" }}>
                  <Mail size={12} /> Connect Gmail
                </button>
              )}
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0"
              style={{ backgroundColor: "rgba(124,144,130,0.18)", color: "#506357" }}>
              <Zap size={18} />
            </span>
            <div>
              <p className="text-xs uppercase tracking-wider" style={{ color: "var(--jaf-text-muted)" }}>Run on demand</p>
              <p className="font-serif text-lg leading-tight">Test generation</p>
              <button type="button" onClick={handleRunNow} disabled={generating} data-testid="run-now-btn"
                className="mt-2 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md font-medium disabled:opacity-60 transition-colors"
                style={{ backgroundColor: "#506357", color: "#fff" }}>
                <Play size={12} /> {generating ? "Generating…" : "Generate now"}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex gap-2" data-testid="admin-tabs">
            <TabBtn id="recipes" current={tab} set={setTab} icon={BookOpen}>
              Recipes {draftCount > 0 && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: "#fff", color: "var(--jaf-primary)" }}>{draftCount}</span>}
            </TabBtn>
            <TabBtn id="subscribers" current={tab} set={setTab} icon={Users}>Subscribers</TabBtn>
            <TabBtn id="pipeline" current={tab} set={setTab} icon={ClipboardList}>Pipeline logs</TabBtn>
            <TabBtn id="settings" current={tab} set={setTab} icon={SettingsIcon}>Settings</TabBtn>
          </div>
          {tab === "recipes" && (
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex gap-1" data-testid="status-filter">
                {["all", "draft", "published"].map((s) => (
                  <button key={s} type="button" onClick={() => setStatusFilter(s)}
                    data-testid={`status-filter-${s}`}
                    className="px-3.5 h-9 rounded-full text-xs font-medium capitalize"
                    style={statusFilter === s
                      ? { backgroundColor: "var(--jaf-text)", color: "#fff" }
                      : { backgroundColor: "#fff", color: "var(--jaf-text)", border: "1px solid var(--jaf-border)" }}>
                    {s}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setEditing("new")} data-testid="new-recipe-btn"
                className="jaf-btn-primary inline-flex items-center gap-2 px-5 h-10 rounded-md text-sm font-medium">
                <Plus size={16} /> New recipe
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="h-40 rounded-xl animate-pulse" style={{ backgroundColor: "var(--jaf-surface)" }} />
        ) : tab === "recipes" ? (
          <RecipesTable recipes={recipes} onEdit={(r) => setEditing(r)} onDelete={onDelete}
            onPublish={onPublish} onUnpublish={onUnpublish} />
        ) : tab === "subscribers" ? (
          <SubscribersTable subscribers={subscribers} onDelete={onDeleteSub} />
        ) : tab === "pipeline" ? (
          <PipelineLogsTable logs={logs} />
        ) : (
          <SettingsPanel />
        )}
      </div>
    </div>
  );
}

function TabBtn({ id, current, set, icon: Icon, children }) {
  const active = current === id;
  return (
    <button type="button" data-testid={`tab-${id}`} onClick={() => set(id)}
      className="inline-flex items-center gap-2 px-5 h-10 rounded-full text-sm font-medium"
      style={active
        ? { backgroundColor: "var(--jaf-primary)", color: "#fff" }
        : { backgroundColor: "var(--jaf-surface)", color: "var(--jaf-text)", border: "1px solid var(--jaf-border)" }}>
      <Icon size={14} /> {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const isDraft = status === "draft";
  return (
    <span className="text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1"
      data-testid={`status-badge-${status}`}
      style={isDraft
        ? { backgroundColor: "rgba(200,90,50,0.12)", color: "var(--jaf-primary)" }
        : { backgroundColor: "rgba(124,144,130,0.18)", color: "#506357" }}>
      {isDraft ? "Draft" : "Live"}
    </span>
  );
}

function RecipesTable({ recipes, onEdit, onDelete, onPublish, onUnpublish }) {
  if (!recipes.length)
    return <p className="text-center py-20" style={{ color: "var(--jaf-text-muted)" }}>No recipes match this filter.</p>;
  return (
    <div className="overflow-x-auto rounded-xl"
      style={{ border: "1px solid var(--jaf-border)", backgroundColor: "#fff" }} data-testid="recipes-table">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: "var(--jaf-surface)" }}>
            <th className="text-left px-5 py-3 font-medium">Recipe</th>
            <th className="text-left px-5 py-3 font-medium">Status</th>
            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Category</th>
            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Time</th>
            <th className="text-right px-5 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {recipes.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid var(--jaf-border)" }}>
              <td className="px-5 py-4">
                <div className="flex items-center gap-3">
                  <img src={r.image_url} alt="" className="h-12 w-12 rounded-md object-cover flex-shrink-0" />
                  <div>
                    <p className="font-medium">{r.title}</p>
                    <p className="text-xs" style={{ color: "var(--jaf-text-muted)" }}>/{r.slug}</p>
                  </div>
                </div>
              </td>
              <td className="px-5 py-4"><StatusBadge status={r.status || "published"} /></td>
              <td className="px-5 py-4 hidden md:table-cell" style={{ color: "var(--jaf-text-muted)" }}>{r.category}</td>
              <td className="px-5 py-4 hidden md:table-cell" style={{ color: "var(--jaf-text-muted)" }}>{r.total_time_minutes} min</td>
              <td className="px-5 py-4">
                <div className="flex justify-end gap-1.5">
                  {(r.status || "published") === "draft" ? (
                    <button type="button" onClick={() => onPublish(r.slug)} data-testid={`publish-${r.slug}`}
                      className="text-xs px-3 h-8 rounded-md font-medium"
                      style={{ backgroundColor: "var(--jaf-primary)", color: "#fff" }}>
                      Publish
                    </button>
                  ) : (
                    <button type="button" onClick={() => onUnpublish(r.slug)} data-testid={`unpublish-${r.slug}`}
                      className="text-xs px-3 h-8 rounded-md font-medium"
                      style={{ border: "1px solid var(--jaf-border)", color: "var(--jaf-text)", backgroundColor: "#fff" }}>
                      Unpublish
                    </button>
                  )}
                  <button type="button" onClick={() => onEdit(r)} data-testid={`edit-${r.slug}`}
                    className="p-2 rounded-md hover:bg-[color:var(--jaf-surface)]" aria-label="Edit">
                    <Edit2 size={15} />
                  </button>
                  <button type="button" onClick={() => onDelete(r.slug)} data-testid={`delete-${r.slug}`}
                    className="p-2 rounded-md hover:bg-red-50" aria-label="Delete" style={{ color: "#b04444" }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubscribersTable({ subscribers, onDelete }) {
  if (!subscribers.length)
    return <p className="text-center py-20" style={{ color: "var(--jaf-text-muted)" }}>No subscribers yet.</p>;
  return (
    <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid var(--jaf-border)", backgroundColor: "#fff" }} data-testid="subscribers-table">
      <p className="px-5 py-3 text-sm" style={{ color: "var(--jaf-text-muted)", borderBottom: "1px solid var(--jaf-border)" }}>
        {subscribers.length} {subscribers.length === 1 ? "subscriber" : "subscribers"}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: "var(--jaf-surface)" }}>
            <th className="text-left px-5 py-3 font-medium">Name</th>
            <th className="text-left px-5 py-3 font-medium">Email</th>
            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Source</th>
            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Joined</th>
            <th className="text-right px-5 py-3 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {subscribers.map((s) => (
            <tr key={s.id} style={{ borderTop: "1px solid var(--jaf-border)" }}>
              <td className="px-5 py-3 font-medium">{s.name}</td>
              <td className="px-5 py-3" style={{ color: "var(--jaf-text-muted)" }}>{s.email}</td>
              <td className="px-5 py-3 hidden md:table-cell" style={{ color: "var(--jaf-text-muted)" }}>{s.source}</td>
              <td className="px-5 py-3 hidden md:table-cell" style={{ color: "var(--jaf-text-muted)" }}>{new Date(s.created_at).toLocaleDateString()}</td>
              <td className="px-5 py-3 text-right">
                <button type="button" onClick={() => onDelete(s.id)} data-testid={`delete-sub-${s.id}`}
                  className="p-2 rounded-md hover:bg-red-50" aria-label="Remove" style={{ color: "#b04444" }}>
                  <Trash2 size={15} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PipelineLogsTable({ logs }) {
  if (!logs.length)
    return <p className="text-center py-20" style={{ color: "var(--jaf-text-muted)" }}>No generation runs yet. The first daily run will appear here after 6am AEST.</p>;
  return (
    <div className="overflow-x-auto rounded-xl" style={{ border: "1px solid var(--jaf-border)", backgroundColor: "#fff" }} data-testid="pipeline-logs">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: "var(--jaf-surface)" }}>
            <th className="text-left px-5 py-3 font-medium">When</th>
            <th className="text-left px-5 py-3 font-medium">Status</th>
            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Category</th>
            <th className="text-left px-5 py-3 font-medium hidden md:table-cell">Recipe</th>
            <th className="text-left px-5 py-3 font-medium hidden lg:table-cell">Trigger</th>
            <th className="text-left px-5 py-3 font-medium hidden lg:table-cell">Email</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} style={{ borderTop: "1px solid var(--jaf-border)" }}>
              <td className="px-5 py-3 font-mono text-xs" style={{ color: "var(--jaf-text-muted)" }}>
                {new Date(l.created_at).toLocaleString()}
              </td>
              <td className="px-5 py-3">
                <LogStatusBadge status={l.status} />
              </td>
              <td className="px-5 py-3 hidden md:table-cell" style={{ color: "var(--jaf-text-muted)" }}>
                {l.category || "—"} <span className="text-xs">({l.day || "—"})</span>
              </td>
              <td className="px-5 py-3 hidden md:table-cell">
                {l.slug ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="truncate">{l.title || l.slug}</span>
                  </span>
                ) : <span style={{ color: "var(--jaf-text-muted)" }}>—</span>}
                {l.error && <p className="text-xs mt-0.5" style={{ color: "#b04444" }}>{l.error}</p>}
              </td>
              <td className="px-5 py-3 hidden lg:table-cell text-xs" style={{ color: "var(--jaf-text-muted)" }}>{l.triggered_by || "—"}</td>
              <td className="px-5 py-3 hidden lg:table-cell text-xs" style={{ color: "var(--jaf-text-muted)" }}>{l.email_status || l.failure_email_status || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LogStatusBadge({ status }) {
  const map = {
    success: { bg: "rgba(124,144,130,0.18)", fg: "#506357", label: "Success" },
    failed: { bg: "rgba(176,68,68,0.12)", fg: "#b04444", label: "Failed" },
    started: { bg: "rgba(200,90,50,0.12)", fg: "var(--jaf-primary)", label: "Running" },
    retry_scheduled: { bg: "rgba(234,180,80,0.15)", fg: "#8a6612", label: "Retry queued" },
  };
  const m = map[status] || { bg: "var(--jaf-surface)", fg: "var(--jaf-text)", label: status };
  return (
    <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}
